/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, MenuItem, Paper } from '@mui/material';
import React from 'react';
import Modal from '../../../../components/Modal';
import { Button } from '../../../../components/Button';
import Typography from '../../../../components/Typography';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { colors } from '@farmlands/mui-theme';
import {
  DesktopDateRangePicker,
  DesktopInStockFilter,
  DesktopPriceRangeFilter,
  StyledMenuItem,
} from './DesktopFilters';
import { ComponentFilters, ProductFilterProps, SelectedFiltersType } from './types';
import { BackButton } from '../../../../components/BackButton';
import { unflattenCategories } from '../../../typesense/utils';

type AdjustedMobileProps = Omit<ProductFilterProps, 'selectedFilters'> & {
  selectedFilters: Partial<SelectedFiltersType>;
};

interface MobileFilterMenuProps extends AdjustedMobileProps {
  handleClose: () => void;
  open: boolean;
  title: string;
  backAction: string;
  children?: React.ReactNode | React.ReactNode[];
  extras?: React.ReactNode | React.ReactNode[];
}

type Tier1CategoryProps = Omit<Omit<AdjustedMobileProps, 'items'>, 'components'> & {
  backAction: string;
};
interface MobileCategoriesMenuProps extends Tier1CategoryProps {
  handleClose: () => void;
}
const MobileCategoriesTier1 = ({
  filters,
  selectedFilters,
  handleClose,
  backAction,
}: MobileCategoriesMenuProps) => {
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    selectedFilters.category?.value?.split('>').at(0) ?? null,
  );

  const [selectedSubCategory, setSelectedSubCategory] = React.useState<string | null>(
    selectedFilters.category?.value?.split('>').at(1) ?? null,
  );
  const unflattenedCategories = unflattenCategories(filters.categories);

  const [state, setState] = React.useState<'tier1' | 'tier2'>('tier1');

  const selectCategory = (category: string) => {
    handleCategoryClick(category);
    if (unflattenedCategories[category].length > 0) {
      setState('tier2');
    }
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);

    selectedFilters.category?.setValue(category);
  };

  const handleSubCategoryClick = (subCategory: string) => {
    if (selectedSubCategory === subCategory) {
      setSelectedSubCategory(null);
    } else {
      setSelectedSubCategory(subCategory);
    }
    if (selectedCategory) {
      selectedFilters.category?.setValue(selectedCategory + '>' + subCategory);
    }
  };

  const title = React.useMemo(() => {
    if (state === 'tier2' && selectedFilters.category?.value?.split('>').at(0)) {
      return selectedFilters.category?.value?.split('>').at(0);
    }
    return 'Category';
  }, [state, selectedFilters.category]);

  return (
    <Box position={'absolute'} display='flex' top={0} right={0} bottom={0} left={0} zIndex={9998}>
      <Paper
        sx={{ paddingTop: '1rem', display: 'flex', flexDirection: 'column', flexGrow: '1' }}
        elevation={0}
      >
        <Box
          zIndex={9999}
          position='sticky'
          top={0}
          pt={'1rem'}
          mt={'-1rem'}
          width='100%'
          height='auto'
          bgcolor={colors.shade0}
        >
          <Paper sx={{ width: '100%' }} elevation={0}>
            <BackButton
              text={backAction}
              onClick={() => (state === 'tier1' ? handleClose() : setState('tier1'))}
            />

            <Typography variant='body2' color='grey800' fontWeight='bold'>
              {title}
            </Typography>
          </Paper>
        </Box>

        {state === 'tier1' && (
          <Box overflow='hidden scroll' display='flex' flexShrink='1' flexDirection='column'>
            {Object.keys(unflattenedCategories).map((category: string) => {
              const selectedCategory =
                selectedFilters.category?.value?.split('>').at(0) === category;
              return (
                <Box
                  key={category}
                  sx={{ padding: 0, bgcolor: 'none' }}
                  onClick={() => selectCategory(category)}
                >
                  <Box
                    display='flex'
                    flexGrow='1'
                    pt={'1rem'}
                    pb={'1rem'}
                    justifyContent={'space-between'}
                    borderBottom={`1px solid ${colors.primary100}`}
                  >
                    <Box display='inline-flex'>
                      <MaterialSymbol
                        sx={{
                          opacity: selectedCategory ? 1 : 0,
                          color: colors.grey800,
                        }}
                      >
                        check
                      </MaterialSymbol>
                      <Typography
                        color={selectedCategory ? 'grey800' : 'primary500'}
                        fontWeight={selectedCategory ? 'bold' : 'inherit'}
                        variant='body2'
                        sx={{ ml: '1rem' }}
                      >
                        {category}
                      </Typography>
                    </Box>
                    {unflattenedCategories[category].length > 0 && (
                      <MaterialSymbol sx={{ transform: 'rotate(-90deg)' }}>
                        expand_more
                      </MaterialSymbol>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {selectedCategory && unflattenedCategories[selectedCategory] && state === 'tier2' && (
          <Box overflow='hidden scroll' display='flex' flexShrink='1' flexDirection='column'>
            {unflattenedCategories[selectedCategory].map((subcategory: string) => (
              <StyledMenuItem
                key={subcategory}
                sx={{ padding: 0, bgcolor: 'none' }}
                onClick={() => handleSubCategoryClick(subcategory)}
              >
                <Box
                  display='flex'
                  flexGrow='1'
                  pt={'1rem'}
                  pb={'1rem'}
                  justifyContent={'space-between'}
                  borderBottom={`1px solid ${colors.primary100}`}
                >
                  <Box display='inline-flex'>
                    <MaterialSymbol
                      sx={{
                        opacity: selectedSubCategory === subcategory ? 1 : 0,
                        color: colors.grey800,
                      }}
                    >
                      check
                    </MaterialSymbol>
                    <Typography
                      color={selectedSubCategory === subcategory ? 'grey800' : 'primary500'}
                      fontWeight={selectedSubCategory === subcategory ? 'bold' : 'inherit'}
                      variant='body2'
                      sx={{ ml: '1rem' }}
                    >
                      {subcategory}
                    </Typography>
                  </Box>
                </Box>
              </StyledMenuItem>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const MobileFilterMenu: React.FC<MobileFilterMenuProps> = ({
  handleClose,
  open,
  selectedFilters,
  filters,
  title,
  components,
  children,
  extras,
  backAction,
}) => {
  const [openCategories, setOpenCategories] = React.useState(false);
  return (
    <Modal
      title={title}
      fullScreen
      scrollable={false}
      open={open}
      handleClose={handleClose}
      footer={
        <Button variant='contained' sx={{ width: '100%' }} onClick={handleClose}>
          View results
        </Button>
      }
    >
      <Box display='flex' flexDirection='column' gap={'2rem'} height={'100%'} position={'relative'}>
        {components.includes(ComponentFilters.CATEGORY) && (
          <>
            {openCategories && (
              <MobileCategoriesTier1
                backAction={backAction}
                filters={filters}
                selectedFilters={selectedFilters}
                handleClose={() => setOpenCategories(false)}
              />
            )}
            <MenuItem sx={{ padding: 0 }} onClick={() => setOpenCategories(true)}>
              <Box
                display='flex'
                flexGrow='1'
                pt={'1rem'}
                pb={'1rem'}
                justifyContent={'space-between'}
                borderBottom={`1px solid ${colors.primary100}`}
              >
                <Typography color='grey800' variant='body2'>
                  Category
                </Typography>
                <MaterialSymbol sx={{ transform: 'rotate(-90deg)' }}>expand_more</MaterialSymbol>
              </Box>
            </MenuItem>
          </>
        )}

        {components.includes(ComponentFilters.PRICE) && selectedFilters.priceRange && (
          <Box display='grid' gap='4px'>
            <DesktopPriceRangeFilter
              selectedFilters={{
                priceRange: selectedFilters.priceRange,
              }}
              label='Price range'
            />
          </Box>
        )}

        {components.includes(ComponentFilters.DATE) && selectedFilters.dateRange && (
          <Box display='grid' gap='4px'>
            <DesktopDateRangePicker
              label='Time period: '
              dateRange={selectedFilters.dateRange.value}
              handleDateRange={selectedFilters.dateRange.setValue}
            />
          </Box>
        )}

        {components.includes(ComponentFilters.IN_STOCK) && selectedFilters.inStock && (
          <Box display='grid' gap='4px'>
            <DesktopInStockFilter
              selectedFilters={{
                inStock: selectedFilters.inStock,
              }}
            />
          </Box>
        )}

        {children}
      </Box>
      {extras}
    </Modal>
  );
};
